import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { FaUserAlt, FaBoxOpen, FaMapMarkerAlt, FaHeart, FaSignOutAlt, FaEdit, FaCheckCircle, FaExclamationCircle, FaSpinner } from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb';
import ShippingInfo from '../components/ShippingInfo';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { getUserProfile } from '../api/authApi';

function MyAccountPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    gender: '0'
  });
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    id: null,
    address: '',
    house: '',
    landmark: '',
    pincode: '',
    title: 'home',
    lat: '',
    lng: ''
  });

  useEffect(() => {
    if (user) {
      loadProfile();
      if (activeTab === 'orders') loadOrders();
      if (activeTab === 'addresses') loadAddresses();
    }
  }, [user, activeTab]);

  const loadProfile = async () => {
    if (!user || !user.id) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const result = await getUserProfile(user.id, token);
      
      if (result.success) {
        setProfileData(result.data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const loadOrders = async () => {
    // TODO: Implement order loading logic
    setOrdersLoading(false);
  };

  const loadAddresses = async () => {
    // TODO: Implement address loading logic
    setAddressesLoading(false);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });
    
    try {
      // TODO: Implement profile update logic
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setEditMode(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    // TODO: Implement image upload logic
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // TODO: Implement address save logic
      setMessage({ type: 'success', text: 'Address saved successfully!' });
      setShowAddressForm(false);
      await loadAddresses();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save address' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAddress = (addr) => {
    setAddressForm(addr);
    setShowAddressForm(true);
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    
    try {
      // TODO: Implement address delete logic
      await loadAddresses();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete address' });
    }
  };

  const getProfileImageUrl = () => {
    return user?.profile_image || '/assets/img/other/account-thumb.webp';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      <Header />
      <main className="main__content_wrapper">
        <Breadcrumb title="My Account" items={[{ label: 'Home', href: '/' }, { label: 'My Account' }]} />
        
        <section className="my__account--section section--padding">
          <div className="container">
            {message.text && (
              <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-3`} role="alert">
                {message.text}
              </div>
            )}
            
            <p className="account__welcome--text">Hello, {user?.first_name || 'User'} welcome to your dashboard!</p>
            
            <div className="my__account--section__inner border-radius-10 d-flex">
              <div className="account__left--sidebar">
                <h2 className="account__content--title h3 mb-20">My Profile</h2>
                <ul className="account__menu">
                  <li className={`account__menu--list ${activeTab === 'profile' ? 'active' : ''}`}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('profile'); }}>Profile</a>
                  </li>
                  <li className={`account__menu--list ${activeTab === 'orders' ? 'active' : ''}`}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('orders'); }}>Orders</a>
                  </li>
                  <li className={`account__menu--list ${activeTab === 'addresses' ? 'active' : ''}`}>
                    <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('addresses'); }}>Addresses</a>
                  </li>
                  <li className="account__menu--list">
                    <a href="/wishlist">Wishlist</a>
                  </li>
                  <li className="account__menu--list">
                    <a href="#" onClick={(e) => { e.preventDefault(); logout(); navigate('/'); }}>Log Out</a>
                  </li>
                </ul>
              </div>
              
              <div className="account__wrapper">
                {/* Profile Tab */}
                {activeTab === 'profile' && (
                  <div className="account__content">
                    <h2 className="account__content--title h3 mb-20">Profile Information</h2>
                    
                    <div className="mb-4 text-center">
                      <div className="position-relative d-inline-block">
                        <img 
                          src={getProfileImageUrl()} 
                          alt="Profile" 
                          style={{ 
                            width: '120px', 
                            height: '120px', 
                            borderRadius: '50%', 
                            objectFit: 'cover',
                            border: '3px solid #f0f0f0'
                          }} 
                        />
                        <label 
                          htmlFor="profileImageInput" 
                          style={{ 
                            position: 'absolute', 
                            bottom: '0', 
                            right: '0', 
                            background: '#ff6b6b', 
                            color: 'white', 
                            borderRadius: '50%', 
                            width: '35px', 
                            height: '35px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            cursor: 'pointer',
                            border: '2px solid white'
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
                          </svg>
                        </label>
                        <input 
                          type="file" 
                          id="profileImageInput" 
                          accept="image/*" 
                          onChange={handleImageUpload} 
                          style={{ display: 'none' }} 
                        />
                      </div>
                    </div>

                    {!editMode ? (
                      <div>
                        <div className="mb-3">
                          <strong>Name:</strong> {profileData.first_name} {profileData.last_name}
                        </div>
                        <div className="mb-3">
                          <strong>Email:</strong> {profileData.email}
                        </div>
                        <div className="mb-3">
                          <strong>Mobile:</strong> {profileData.mobile}
                        </div>
                        <div className="mb-3">
                          <strong>Gender:</strong> {profileData.gender === '1' ? 'Male' : profileData.gender === '2' ? 'Female' : 'Other'}
                        </div>
                        <button 
                          className="continue__shipping--btn primary__btn border-radius-5" 
                          onClick={() => setEditMode(true)}
                        >
                          Edit Profile
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleProfileUpdate}>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="checkout__input--label">First Name <span className="checkout__input--label__star">*</span></label>
                            <input 
                              className="checkout__input--field border-radius-5" 
                              type="text" 
                              value={profileData.first_name}
                              onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="checkout__input--label">Last Name <span className="checkout__input--label__star">*</span></label>
                            <input 
                              className="checkout__input--field border-radius-5" 
                              type="text" 
                              value={profileData.last_name}
                              onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                              required
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="checkout__input--label">Mobile <span className="checkout__input--label__star">*</span></label>
                            <input 
                              className="checkout__input--field border-radius-5" 
                              type="tel" 
                              value={profileData.mobile}
                              onChange={(e) => setProfileData({...profileData, mobile: e.target.value})}
                              required
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="checkout__input--label">Gender</label>
                            <select 
                              className="checkout__input--field border-radius-5" 
                              value={profileData.gender}
                              onChange={(e) => setProfileData({...profileData, gender: e.target.value})}
                            >
                              <option value="0">Other</option>
                              <option value="1">Male</option>
                              <option value="2">Female</option>
                            </select>
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <button 
                            type="submit" 
                            className="continue__shipping--btn primary__btn border-radius-5"
                            disabled={loading}
                          >
                            {loading ? 'Updating...' : 'Update Profile'}
                          </button>
                          <button 
                            type="button" 
                            className="continue__shipping--btn btn-secondary border-radius-5"
                            onClick={() => { setEditMode(false); loadProfile(); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                  <div className="account__content">
                    <h2 className="account__content--title h3 mb-20">Orders History</h2>
                    {ordersLoading ? (
                      <p>Loading orders...</p>
                    ) : orders.length === 0 ? (
                      <p>No orders found</p>
                    ) : (
                      <div className="account__table--area">
                        <table className="account__table">
                          <thead className="account__table--header">
                            <tr className="account__table--header__child">
                              <th className="account__table--header__child--items">Order ID</th>
                              <th className="account__table--header__child--items">Date</th>
                              <th className="account__table--header__child--items">Status</th>
                              <th className="account__table--header__child--items">Total</th>
                              <th className="account__table--header__child--items">Action</th>
                            </tr>
                          </thead>
                          <tbody className="account__table--body">
                            {orders.map((order) => (
                              <tr key={order.id} className="account__table--body__child">
                                <td className="account__table--body__child--items">#{order.id}</td>
                                <td className="account__table--body__child--items">{formatDate(order.created_at)}</td>
                                <td className="account__table--body__child--items">
                                  <span className={`badge ${order.status === 'delivered' ? 'bg-success' : order.status === 'cancelled' ? 'bg-danger' : 'bg-warning'}`}>
                                    {order.status}
                                  </span>
                                </td>
                                <td className="account__table--body__child--items">${order.grand_total}</td>
                                <td className="account__table--body__child--items">
                                  <button 
                                    className="btn btn-sm btn-primary"
                                    onClick={() => navigate(`/order-detail/${order.id}`)}
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Addresses Tab */}
                {activeTab === 'addresses' && (
                  <div className="account__content">
                    <div className="d-flex justify-content-between align-items-center mb-20">
                      <h2 className="account__content--title h3 mb-0">My Addresses</h2>
                      <button 
                        className="continue__shipping--btn primary__btn border-radius-5"
                        onClick={() => {
                          setAddressForm({
                            id: null,
                            address: '',
                            house: '',
                            landmark: '',
                            pincode: '',
                            title: 'home',
                            lat: '',
                            lng: ''
                          });
                          setShowAddressForm(true);
                        }}
                      >
                        Add New Address
                      </button>
                    </div>

                    {showAddressForm && (
                      <div className="mb-4 p-4" style={{ background: '#f9f9f9', borderRadius: '8px' }}>
                        <h4>{addressForm.id ? 'Edit Address' : 'Add New Address'}</h4>
                        <form onSubmit={handleAddressSubmit}>
                          <div className="row mb-3">
                            <div className="col-md-12">
                              <label className="checkout__input--label">Address <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.address}
                                onChange={(e) => setAddressForm({...addressForm, address: e.target.value})}
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label className="checkout__input--label">House/Flat No <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.house}
                                onChange={(e) => setAddressForm({...addressForm, house: e.target.value})}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="checkout__input--label">Landmark <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.landmark}
                                onChange={(e) => setAddressForm({...addressForm, landmark: e.target.value})}
                                required
                              />
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-md-6">
                              <label className="checkout__input--label">Pincode <span className="checkout__input--label__star">*</span></label>
                              <input 
                                className="checkout__input--field border-radius-5" 
                                type="text" 
                                value={addressForm.pincode}
                                onChange={(e) => setAddressForm({...addressForm, pincode: e.target.value})}
                                required
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="checkout__input--label">Title</label>
                              <select 
                                className="checkout__input--field border-radius-5" 
                                value={addressForm.title}
                                onChange={(e) => setAddressForm({...addressForm, title: e.target.value})}
                              >
                                <option value="home">Home</option>
                                <option value="work">Work</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <button 
                              type="submit" 
                              className="continue__shipping--btn primary__btn border-radius-5"
                              disabled={loading}
                            >
                              {loading ? 'Saving...' : addressForm.id ? 'Update Address' : 'Add Address'}
                            </button>
                            <button 
                              type="button" 
                              className="continue__shipping--btn btn-secondary border-radius-5"
                              onClick={() => setShowAddressForm(false)}
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {addressesLoading ? (
                      <p>Loading addresses...</p>
                    ) : addresses.length === 0 ? (
                      <p>No addresses found</p>
                    ) : (
                      <div className="row">
                        {addresses.map((addr) => (
                          <div key={addr.id} className="col-md-6 mb-3">
                            <div className="p-3" style={{ border: '1px solid #ddd', borderRadius: '8px' }}>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h5 className="text-capitalize">{addr.title}</h5>
                                <div>
                                  <button 
                                    className="btn btn-sm btn-link p-0 me-2" 
                                    onClick={() => handleEditAddress(addr)}
                                    title="Edit"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                      <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10z"/>
                                    </svg>
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-link p-0 text-danger" 
                                    onClick={() => handleDeleteAddress(addr.id)}
                                    title="Delete"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              <p className="mb-1">{addr.house}, {addr.landmark}</p>
                              <p className="mb-1">{addr.address}</p>
                              <p className="mb-0"><strong>Pincode:</strong> {addr.pincode}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <ShippingInfo />
      </main>

      <Footer />
    </>
  );
}

export default MyAccountPage;
